import { useState, useEffect } from 'react';

interface IframeDetectionResult {
  isInIframe: boolean;
  iframeDomain: string | null;
  canAccessParent: boolean;
  parentUrl: string | null;
  appId: string;
  clientKey: string;
  modelId: string;
}

export const useIframeDetection = (): IframeDetectionResult => {
  const [iframeInfo, setIframeInfo] = useState<IframeDetectionResult>({
    isInIframe: true,
    iframeDomain: null,
    canAccessParent: false,
    parentUrl: null,
    appId: "",
    clientKey: "",
    modelId: ""
  });

  // Función para extraer parámetros del hash de la URL
  const parseHashParams = (url: string = window.location.href): { appId: string; clientKey: string; modelId: string } => {
    try {
      const hashIndex = url.indexOf('#');
      if (hashIndex === -1) {
        return { appId: "", clientKey: "", modelId: "" };
      }

      const hashPart = url.substring(hashIndex + 1);
      
      // Formato esperado: appId|clientKey
      if (hashPart.includes('|')) {
        const [appId, clientKey, modelId] = hashPart.split('|');
        return { 
          appId: appId.trim(), 
          clientKey: clientKey.trim(), 
          modelId: modelId.trim() 
        };
      }

      return { appId: "", clientKey: "", modelId: "" };
    } catch (error) {
      console.error('Error parsing hash parameters:', error);
      return { appId: "", clientKey: "", modelId: "" };
    }
  };

  useEffect(() => {
    const detectIframe = () => {
      try {
        // Método 1: Comparar window con window.parent
        const isInIframe = window !== window.parent;
        
        let canAccessParent = false;
        let parentUrl: string | null = null;
        let iframeDomain: string | null = null;

        // Extraer parámetros del hash de la URL actual
        const { appId, clientKey, modelId } = parseHashParams();

        if (isInIframe) {
          try {
            // Intentar acceder al parent para verificar si hay restricciones CORS
            parentUrl = window.parent.location.href;
            canAccessParent = true;
            
            // Extraer dominio del parent
            const parentDomain = new URL(parentUrl).hostname;
            iframeDomain = parentDomain;
          } catch (error) {
            // Si no se puede acceder al parent por CORS, aún estamos en iframe
            canAccessParent = false;
            console.log('Iframe detectado pero sin acceso al parent (CORS restriction)');
            
            try {
              // Intentar obtener información del referrer
              if (document.referrer) {
                const referrerUrl = new URL(document.referrer);
                iframeDomain = referrerUrl.hostname;
              }
            } catch (e) {
              console.log('No se pudo obtener información del referrer');
            }
          }
        }

        setIframeInfo({
          isInIframe,
          iframeDomain,
          canAccessParent,
          parentUrl,
          appId,
          clientKey,
          modelId
        });

      } catch (error) {
        console.error('Error detectando iframe:', error);
        const { appId, clientKey, modelId } = parseHashParams();
        setIframeInfo({
          isInIframe: false,
          iframeDomain: null,
          canAccessParent: false,
          parentUrl: null,
          appId,
          clientKey,
          modelId
        });
      }
    };

    detectIframe();

  }, []);

  return iframeInfo;
};